import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { useEditorSelector } from "@v1/store/store";
import {
  getElementAbsoluteMargins,
  getElementMargin,
  getElementPaddings,
} from "@v2/utils/dom";

import { IGNORE_CURSOR_CLASS } from "./candidateNode";
import { selectCanvasRelativeBoundingClientRect } from "./canvas-reducer";

const getBorderStyles = (
  node: Element,
  type: "margin" | "padding",
): React.CSSProperties => {
  const bounds =
    type === "margin"
      ? getElementAbsoluteMargins(node)
      : getElementPaddings(node);
  return {
    borderWidth: `${bounds.top} ${bounds.right} ${bounds.bottom} ${bounds.left}`,
  };
};

/**
 * Displays padding and margin indicators for a given element.
 *
 * TODO (Noah, 2022-01-28): This component should be responsible for calculating
 * the indicator position/bounds by itself, there's no reason for these styles
 * to be passed in as props (at least, I don't think)
 */
export const SpacingIndicators: React.FC<{
  node: Element;
}> = (props) => {
  const getCanvasRelativeBoundingClientRect = useEditorSelector(
    selectCanvasRelativeBoundingClientRect,
  );
  const margin = {
    top: Number.parseInt(getElementMargin("top", props.node), 10),
    left: Number.parseInt(getElementMargin("left", props.node), 10),
    bottom: Number.parseInt(getElementMargin("bottom", props.node), 10),
    right: Number.parseInt(getElementMargin("right", props.node), 10),
  };
  const {
    top: boundingTop,
    left: boundingLeft,
    width: boundingWidth,
    height: boundingHeight,
  } = getCanvasRelativeBoundingClientRect(props.node);

  const style = getComputedStyle(props.node);

  const marginIndicatorBounds = {
    top: boundingTop - Math.max(margin.top, 0),
    height:
      boundingHeight + Math.max(margin.top, 0) + Math.max(margin.bottom, 0),
    left: boundingLeft - Math.max(margin.left, 0),
    width: boundingWidth + Math.max(margin.left, 0) + Math.max(margin.right, 0),
  };

  const paddingIndicatorBounds = {
    top: `calc(${style.borderTopWidth} + min(0px, ${margin.top}px))`,
    left: `calc(${style.borderLeftWidth} + min(0px, ${margin.left}px))`,
    width: `calc(${boundingWidth}px - ${style.borderLeftWidth} - ${style.borderRightWidth})`,
    height: `calc(${boundingHeight}px - ${style.borderTopWidth} - ${style.borderBottomWidth})`,
  };

  return (
    <div
      className={twMerge(
        IGNORE_CURSOR_CLASS,
        // eslint-disable-next-line replo/no-tailwind-literal-color
        "border-soli border-sky-600/15 z-1 pointer-events-none absolute cursor-default",
      )}
      style={{
        ...marginIndicatorBounds,
        ...getBorderStyles(props.node, "margin"),
      }}
    >
      <div
        className={twMerge(
          IGNORE_CURSOR_CLASS,
          // eslint-disable-next-line replo/no-tailwind-literal-color
          "border-green-500/15 pointer-events-none absolute cursor-default border-solid",
        )}
        style={{
          ...paddingIndicatorBounds,
          ...getBorderStyles(props.node, "padding"),
        }}
      />
    </div>
  );
};
